import { Title, useLoading } from '@teko/ds-desktop';
import { Terminal } from '@teko/order';
import { styled } from '@teko/ui';
import {
  ModalErrorTerminal,
  PopupThreeLevelLocationPicker,
} from 'components/ThreeLevelLocation';
import { ModalUnAvailableTerminal } from 'components/ThreeLevelLocation/ModalUnAvailableTerminal';
import { useServices } from 'contexts';
import { useGlobalData } from 'contexts/GlobalDataContext';
import TerraKit from '@terra-js/terra-kit';

import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import {
  threeLevelLocationReducer,
  initThreeLevelLocation,
  ThreeLevelLocation,
  TYPE_REDUCER,
  LevelLocation,
} from './reducer';
import {
  clearCacheAndReloadPage,
  getUserSelectedInfo,
  reloadPage,
  setUserSelectedInfo,
  getJson,
  removeUserSelectedInfo,
} from 'helpers';
import {
  PROVINCE,
  UPDATE_VALUE_DISTRICT,
  UPDATE_VALUE_PROVINCE,
  UPDATE_VALUE_WARD,
  userSelectedConstants,
} from '../../constants';
import { APIResponse } from '@teko/api';
import { trackCustomEvent } from '@teko/tracking';

export * from './reducer';

export interface ThreeLevelLocationContext {
  showThreeLevelLocationPicker: boolean;
  onCloseThreeLevelLocationPopup: () => void;
  onOpenThreeLevelLocationPopup?: () => void;
  threeLevelLocation: ThreeLevelLocation;
  updateThreeLevelLocation?: (action: {
    type: TYPE_REDUCER;
    value: LevelLocation;
  }) => void;
  stepLocation?: STEP_LOCATION;
  setStepLocation?: (stepLocation: STEP_LOCATION) => void;
  onSubmitNewLocation?: (
    newThreeLevelLocation: ThreeLevelLocation,
  ) => Promise<void>;
  correctThreeLevelLocation?: ThreeLevelLocation | null;
  handleErrorTerminal?: () => void;
  handleUnAvailableTerminal?: () => void;
  setLoadingThreeLevelLocation?: (loading: boolean) => void;
  setNewTerminal?: (
    terminal: Terminal,
    newThreeLevelLocation: ThreeLevelLocation,
    noNeedReload?: boolean,
    reloadUrl?: string,
  ) => void;
  currentTerminal?: Terminal;
  setNewSupermarketTerminal?: (
    terminal: Terminal,
    noNeedReload?: boolean,
    reloadUrl?: string,
  ) => void;
  requestGetTerminal?: (wardCode: string) => Promise<APIResponse<Terminal>>;
  unavailableThreeLevelLocation?: ThreeLevelLocation;
  setUnavailableThreeLevelLocation?: (
    unavailableThreeLevelLocation: ThreeLevelLocation,
  ) => void;
  currentSiteId?: number;
  setCurrentSiteId?: (siteId: number) => void;
}

const ThreeLevelLocationContext = createContext<ThreeLevelLocationContext>({
  showThreeLevelLocationPicker: false,
  onCloseThreeLevelLocationPopup: () => {},
  threeLevelLocation: initThreeLevelLocation,
});

interface ThreeLevelLocationProviderProps {
  children: React.ReactNode;
}

export type STEP_LOCATION = 'PROVINCE' | 'DISTRICT' | 'WARD';

export const ThreeLevelLocationProvider: React.FC<
  ThreeLevelLocationProviderProps
> = ({ children }) => {
  const {
    isMobile,
    clearCacheAfterSwitchLocation,
    epicOmni1541,
    epicOmni1584,
    isDmsFlow,
    platformId,
    isAllowGetTerminalFromTerra,
    terminalId,
    epicOmni1584Phase2,
  } = useGlobalData();
  const { orderService } = useServices();

  const [, setLoading] = useLoading('threeLevelLocation');
  const [showThreeLevelLocationPicker, setShowThreeLevelLocationPicker] =
    useState<boolean>(false);
  const [showErrorUnavailableTerminal, setShowErrorUnavailableTerminal] =
    useState(false);
  const [unavailableThreeLevelLocation, setUnavailableThreeLevelLocation] =
    useState<ThreeLevelLocation>({
      ward: { code: '', label: '', id: 0 },
      district: { code: '', label: '', id: 0 },
      province: { code: '', label: '', id: 0 },
    });
  const [showErrorTerminal, setShowErrorTerminal] = useState(false);
  const [threeLevelLocation, dispatch] = useReducer<
    React.Reducer<
      ThreeLevelLocation,
      { type: TYPE_REDUCER; value: LevelLocation }
    >
  >(threeLevelLocationReducer, initThreeLevelLocation);
  const [correctThreeLevelLocation, setCorrectThreeLevelLocation] =
    useState<ThreeLevelLocation | null>(null);
  const [currentTerminal, setCurrentTerminal] = useState<Terminal | undefined>(
    undefined,
  );
  const [currentSiteId, setCurrentSiteId] = useState<number | undefined>(
    undefined,
  );
  const [stepLocation, setStepLocation] = useState<STEP_LOCATION>(PROVINCE);

  const onCloseThreeLevelLocationPopup = () => {
    if (!correctThreeLevelLocation) {
      return;
    }
    setShowThreeLevelLocationPicker(false);
  };

  const onOpenThreeLevelLocationPopup = () => {
    if (!!correctThreeLevelLocation) {
      dispatch({
        type: UPDATE_VALUE_PROVINCE,
        value: {
          label: correctThreeLevelLocation.province.label,
          code: correctThreeLevelLocation.province.code,
          id: correctThreeLevelLocation.province.id,
        },
      });
      dispatch({
        type: UPDATE_VALUE_DISTRICT,
        value: {
          label: correctThreeLevelLocation.district?.label || '',
          code: correctThreeLevelLocation.district?.code || '',
          id: correctThreeLevelLocation.district?.id || 0,
        },
      });
      dispatch({
        type: UPDATE_VALUE_WARD,
        value: {
          label: correctThreeLevelLocation.ward.label,
          code: correctThreeLevelLocation.ward.code,
          id: correctThreeLevelLocation.ward.id,
        },
      });
    }
    setShowThreeLevelLocationPicker(true);
  };

  const getAndSaveTerminalFromStorage = async () => {
    trackCustomEvent('Start get data from storage', {
      action: 'get data from terra',
      label: 'Huy Test',
    });
    if (TerraKit.isAvailable() && isAllowGetTerminalFromTerra) {
      trackCustomEvent(
        'Start get terminal Data FromApp when turn on flag 1584',
        {},
      );
      const terminalDataFromApp = await TerraKit.erp().getTerminal();
      trackCustomEvent('Terminal Data FromApp when turn on flag 1584', {
        action: 'get data from terra',
        label: JSON.stringify(terminalDataFromApp),
      });
      if (!!terminalDataFromApp && terminalId !== terminalDataFromApp.id) {
        trackCustomEvent(
          'Start save Terminal Data FromApp when turn on flag 1584',
          {
            action: 'get data from terra',
            label: `${terminalDataFromApp} + ${terminalDataFromApp.id}`,
          },
        );
        setNewSupermarketTerminal({
          terminalCode: terminalDataFromApp.code,
          terminalId: terminalDataFromApp.id,
          terminalName: terminalDataFromApp.name || '',
        });
      }
      return;
    }
    const terminalCodeFromStorage = getUserSelectedInfo(
      userSelectedConstants.TERMINAL,
      isMobile,
      true,
    );
    const terminalNameFromStorage = getUserSelectedInfo(
      userSelectedConstants.TERMINAL_NAME,
      isMobile,
      true,
    );
    const terminalIdFromStorage = getUserSelectedInfo(
      userSelectedConstants.TERMINAL_ID,
      isMobile,
      true,
    );
    if (!!terminalCodeFromStorage && !!terminalIdFromStorage) {
      setCurrentTerminal({
        terminalCode: terminalCodeFromStorage,
        terminalId: getJson(terminalIdFromStorage),
        terminalName: terminalNameFromStorage || '',
      });
    }
  };

  useEffect(() => {
    const jsonThreeLevelLocationFromStorage = getUserSelectedInfo(
      userSelectedConstants.THREE_LEVEL_LOCATION,
      isMobile,
      true,
    );
    const jsonTerminalIdFromStorage = getUserSelectedInfo(
      userSelectedConstants.TERMINAL_ID,
      isMobile,
      true,
    );
    const jsonTerminalFromStorage = getUserSelectedInfo(
      userSelectedConstants.TERMINAL,
      isMobile,
      true,
    );
    if (!isDmsFlow && epicOmni1541 && epicOmni1584) {
      getAndSaveTerminalFromStorage();
      if (
        !!jsonThreeLevelLocationFromStorage &&
        !!jsonTerminalIdFromStorage &&
        !!jsonTerminalFromStorage
      ) {
        const threeLevelLocationFromStorage = getJson(
          jsonThreeLevelLocationFromStorage,
        );
        if (!!threeLevelLocationFromStorage) {
          setCorrectThreeLevelLocation(threeLevelLocationFromStorage);
          dispatch({
            type: UPDATE_VALUE_PROVINCE,
            value: threeLevelLocationFromStorage.province,
          });
          dispatch({
            type: UPDATE_VALUE_DISTRICT,
            value: threeLevelLocationFromStorage.district,
          });
          dispatch({
            type: UPDATE_VALUE_WARD,
            value: threeLevelLocationFromStorage.ward,
          });
        }
      } else {
        // trong TH la webview thi se khong can hien popup ma tuyen tu app xuong
        // duoc xu ly o ham getAndSaveTerminalFromStorage o tren
        if (!TerraKit.isAvailable()) {
          onOpenThreeLevelLocationPopup();
        }
      }
    } else {
      // only remove location & terminal info and reload page If level location is set before (epicOmni1584 & epicOmni1541 were enabled before)
      if (!!jsonThreeLevelLocationFromStorage && !epicOmni1584Phase2) {
        removeUserSelectedInfo(userSelectedConstants.TERMINAL, isMobile);
        removeUserSelectedInfo(userSelectedConstants.TERMINAL_ID, isMobile);
        removeUserSelectedInfo(userSelectedConstants.TERMINAL_NAME, isMobile);
        removeUserSelectedInfo(
          userSelectedConstants.THREE_LEVEL_LOCATION,
          isMobile,
        );
        removeUserSelectedInfo(userSelectedConstants.LOCATION, isMobile);
        reloadPage();
      }
    }
  }, []);

  const selectTerminal = (
    terminal: Terminal,
    newThreeLevelLocation: ThreeLevelLocation,
  ) => {
    setUserSelectedInfo(
      userSelectedConstants.TERMINAL,
      terminal.terminalCode,
      isMobile,
      true,
    );
    setUserSelectedInfo(
      userSelectedConstants.TERMINAL_NAME,
      terminal.terminalName,
      isMobile,
      true,
    );
    setUserSelectedInfo(
      userSelectedConstants.TERMINAL_ID,
      terminal?.terminalId?.toString(),
      isMobile,
      true,
    );
    setUserSelectedInfo(
      userSelectedConstants.THREE_LEVEL_LOCATION,
      JSON.stringify(newThreeLevelLocation),
      isMobile,
      true,
    );
    setUserSelectedInfo(
      userSelectedConstants.LOCATION,
      newThreeLevelLocation.ward.code,
      isMobile,
      true,
    );
    setCorrectThreeLevelLocation(newThreeLevelLocation);
    setCurrentTerminal(terminal);
  };

  const handleUnAvailableTerminal = () => {
    setShowThreeLevelLocationPicker(false);
    setShowErrorUnavailableTerminal(true);
  };

  const handleErrorTerminal = () => {
    setShowThreeLevelLocationPicker(false);
    setShowErrorTerminal(true);
  };

  const setNewTerminal = (
    terminal: Terminal,
    newThreeLevelLocation: ThreeLevelLocation,
    noNeedReload?: boolean,
    reloadUrl?: string,
  ) => {
    selectTerminal(terminal, newThreeLevelLocation);
    if (!noNeedReload) {
      if (clearCacheAfterSwitchLocation) {
        clearCacheAndReloadPage(reloadUrl);
      } else {
        reloadPage(reloadUrl);
      }
    }
  };

  const requestGetTerminal = async (wardCode: string) => {
    if (epicOmni1584) {
      return await orderService.getPriorityTerminal({
        wardCode,
        platformId,
      });
    }

    return await orderService.getTerminal({
      wardCode,
      platformId,
    });
  };

  const onSubmitNewLocation = async (
    newThreeLevelLocation: ThreeLevelLocation,
  ) => {
    try {
      setLoading(true);
      const response = await requestGetTerminal(
        newThreeLevelLocation.ward.code,
      );
      setLoading(false);
      if (!!response.data.terminalId) {
        setNewTerminal(response.data, newThreeLevelLocation);
      } else {
        setUnavailableThreeLevelLocation(newThreeLevelLocation);
        handleUnAvailableTerminal();
      }
    } catch (error) {
      setUnavailableThreeLevelLocation(newThreeLevelLocation);
      handleUnAvailableTerminal();
      setLoading(false);
    }
  };

  const setNewSupermarketTerminal = (
    terminal: Terminal,
    noNeedReload?: boolean,
    reloadUrl?: string,
  ) => {
    setUserSelectedInfo(
      userSelectedConstants.TERMINAL,
      terminal.terminalCode,
      isMobile,
      true,
    );
    setUserSelectedInfo(
      userSelectedConstants.TERMINAL_NAME,
      terminal.terminalName,
      isMobile,
      true,
    );
    setUserSelectedInfo(
      userSelectedConstants.TERMINAL_ID,
      terminal?.terminalId?.toString(),
      isMobile,
      true,
    );
    setCurrentTerminal(terminal);

    if (!noNeedReload) {
      if (clearCacheAfterSwitchLocation) {
        clearCacheAndReloadPage(reloadUrl);
      } else {
        reloadPage(reloadUrl);
      }
    }
  };

  return (
    <ThreeLevelLocationContext.Provider
      value={{
        showThreeLevelLocationPicker: showThreeLevelLocationPicker,
        onCloseThreeLevelLocationPopup,
        onOpenThreeLevelLocationPopup,
        threeLevelLocation,
        updateThreeLevelLocation: dispatch,
        stepLocation,
        setStepLocation,
        onSubmitNewLocation,
        setLoadingThreeLevelLocation: setLoading,
        correctThreeLevelLocation,
        handleErrorTerminal,
        handleUnAvailableTerminal,
        setNewTerminal,
        setNewSupermarketTerminal,
        currentTerminal,
        requestGetTerminal,
        unavailableThreeLevelLocation,
        setUnavailableThreeLevelLocation,
        currentSiteId,
        setCurrentSiteId,
      }}
    >
      {children}
      {!isDmsFlow && !!epicOmni1541 && (
        <PopupThreeLevelLocationPicker
          showPopup={showThreeLevelLocationPicker}
          onClosePopup={onCloseThreeLevelLocationPopup}
          threeLevelLocation={threeLevelLocation}
          updateThreeLevelLocation={dispatch}
          stepLocation={stepLocation}
          setStepLocation={setStepLocation}
          onSubmitNewLocation={onSubmitNewLocation}
          correctThreeLevelLocation={correctThreeLevelLocation}
        />
      )}
      {!isDmsFlow && !!epicOmni1541 && (
        <ModalUnAvailableTerminal
          showErrorUnavailableTerminal={showErrorUnavailableTerminal}
          setShowErrorUnavailableTerminal={setShowErrorUnavailableTerminal}
          onOK={() => {
            setStepLocation(PROVINCE);
            onOpenThreeLevelLocationPopup();
          }}
        />
      )}
      {!isDmsFlow && !!epicOmni1541 && (
        <ModalErrorTerminal
          showErrorTerminal={showErrorTerminal}
          setShowErrorTerminal={setShowErrorTerminal}
          onOK={() => {
            setShowErrorTerminal(false);
            onSubmitNewLocation(threeLevelLocation);
          }}
          onCancel={() => setShowErrorTerminal(false)}
        />
      )}
    </ThreeLevelLocationContext.Provider>
  );
};

export const useThreeLevelLocationContext = () => {
  return useContext(ThreeLevelLocationContext);
};

export const StyledModalTitle = styled(Title)`
  font-size: 1.2rem;
`;
